/**
 * @cfleet/customer - Main Application Component
 *
 * This is the main entry point for the customer portal.
 * Allows customers to place orders, track deliveries, view invoices,
 * and manage their relationship with dispatch organizations.
 *
 * Features:
 * - Authentication via @cfleet/auth (Better Auth)
 * - Protected routes requiring authentication
 * - Multi-tenant support (customers can belong to multiple organizations)
 * - React Router for navigation
 * - TanStack Query for data fetching
 */

import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, useAuth, type AuthProviderType } from '@cfleet/auth';
import { useCustomerOrganizations, type CustomerOrganization } from '@cfleet/api-client';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import {
  Menu,
  LayoutDashboard,
  PlusCircle,
  Settings as SettingsIcon,
  LogOut,
  Receipt,
  Building2,
  ChevronRight,
  ChevronDown,
  Loader2,
  Command,
} from 'lucide-react';

// Auth components
import SignIn from '@/components/SignIn';
import ForgotPassword from '@/components/ForgotPassword';
import ResetPassword from '@/components/ResetPassword';
import AcceptInvite from '@/components/AcceptInvite';
import VerifyEmail from '@/components/VerifyEmail';

// Portal components
import { Sidebar } from '@/components/Sidebar';
import { OrderList } from '@/components/OrderList';
import { CreateOrder } from '@/components/CreateOrder';
import { OrderDetails } from '@/components/OrderDetails';
import { InvoiceList } from '@/components/InvoiceList';
import { OrganizationList } from '@/components/OrganizationList';
import { OrganizationDetails } from '@/components/OrganizationDetails';
import { Settings } from '@/components/Settings';

// Types
import { ViewState } from '@/types';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// API configuration (will use environment variable in production)
// Uses proxy in development, direct URL in production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Auth provider configuration
// - 'better-auth': Cookie-based auth via Better Auth sidecar (default, for all environments)
// - 'local': Direct API calls with bearer tokens (for unit tests only)
const AUTH_PROVIDER = (import.meta.env.VITE_AUTH_PROVIDER || 'better-auth') as AuthProviderType;
const BETTER_AUTH_URL = import.meta.env.VITE_BETTER_AUTH_URL || '';

/**
 * Customer Portal Component
 *
 * Main application layout with sidebar navigation and content area.
 * Organizations are now fetched via API hooks.
 * Orders are fetched via API hooks in OrderList component.
 * Invoices are fetched via API hooks in InvoiceList component.
 */
function CustomerPortal() {
  const { logout } = useAuth();
  const authConfig = useAuthConfig();

  // View state management
  const [currentView, setView] = useState<ViewState>('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileOrgDropdown, setShowMobileOrgDropdown] = useState(false);

  // Fetch organizations from API for Sidebar and CreateOrder
  const { data: orgsData, isLoading: orgsLoading } = useCustomerOrganizations(
    { per_page: 50 },
    { authConfig }
  );

  const organizations = orgsData?.data ?? [];

  // Selection state
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [initialOrgForNewOrder, setInitialOrgForNewOrder] = useState<string>('');

  // Handlers
  const handleCreateOrderSuccess = () => {
    // Navigate back to dashboard after successful order creation
    setView('dashboard');
  };

  const handleStartNewOrder = (orgId: string) => {
    setInitialOrgForNewOrder(orgId);
    setView('create_order');
  };

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setView('order_details');
  };

  const handleViewOrg = (orgId: string) => {
    setSelectedOrgId(orgId);
    setView('organization_details');
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Render content based on current view
  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return <OrderList onSelectOrder={handleViewOrder} />;

      case 'create_order':
        return (
          <CreateOrder
            onCancel={() => setView('dashboard')}
            onSubmit={handleCreateOrderSuccess}
            organizations={organizations}
            initialOrgId={initialOrgForNewOrder}
          />
        );

      case 'order_details':
        if (selectedOrderId) {
          return (
            <OrderDetails
              orderId={selectedOrderId}
              onBack={() => {
                setSelectedOrderId(null);
                setView('dashboard');
              }}
            />
          );
        }
        return null;

      case 'organizations':
        return <OrganizationList onSelectOrg={handleViewOrg} onNewOrder={handleStartNewOrder} />;

      case 'organization_details':
        if (selectedOrgId) {
          return (
            <OrganizationDetails
              orgId={selectedOrgId}
              onBack={() => {
                setSelectedOrgId(null);
                setView('organizations');
              }}
              onNewOrder={handleStartNewOrder}
            />
          );
        }
        return null;

      case 'invoices':
        return <InvoiceList />;

      case 'settings':
        return <Settings />;

      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-white text-zinc-900 font-sans selection:bg-blue-100">
      <Sidebar
        currentView={currentView}
        setView={setView}
        onSignOut={handleSignOut}
        organizations={organizations}
        organizationsLoading={orgsLoading}
        onNewOrder={handleStartNewOrder}
      />

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 lg:hidden"
          onClick={() => { setIsMobileMenuOpen(false); setShowMobileOrgDropdown(false); }}
        >
          <div
            className="w-64 h-full bg-zinc-925 border-r border-zinc-800 flex flex-col overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                <Command size={14} className="text-white" />
              </div>
              <span className="font-semibold text-zinc-100 tracking-tight">cfleet client</span>
            </div>

            <div className="px-2 flex-1">
              {/* New Order with org dropdown */}
              <button
                onClick={() => setShowMobileOrgDropdown(!showMobileOrgDropdown)}
                className={`w-full mb-1 flex items-center justify-between px-3 py-2 rounded-md transition-all text-sm font-medium border
                  ${showMobileOrgDropdown
                    ? 'bg-blue-600 text-white border-blue-500'
                    : 'bg-blue-600/10 text-blue-400 border-blue-600/20 hover:bg-blue-600/20'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <PlusCircle size={16} />
                  <span>New Order</span>
                </div>
                {showMobileOrgDropdown ? <ChevronDown size={14} /> : <ChevronRight size={14} className="opacity-50" />}
              </button>
              {showMobileOrgDropdown && (
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg py-1 mb-2 mx-1 overflow-hidden">
                  <div className="px-3 py-1.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest border-b border-zinc-800/50 mb-1">
                    Select Dispatch Partner
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {orgsLoading ? (
                      <div className="px-3 py-4 flex items-center justify-center">
                        <Loader2 size={16} className="animate-spin text-zinc-500" />
                        <span className="ml-2 text-xs text-zinc-500">Loading...</span>
                      </div>
                    ) : organizations.filter((o) => o.status === 'active').length > 0 ? (
                      organizations.filter((o) => o.status === 'active').map((org) => (
                        <button
                          key={org.id}
                          onClick={() => {
                            handleStartNewOrder(org.id);
                            setShowMobileOrgDropdown(false);
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-blue-600 hover:text-white transition-colors text-left"
                        >
                          <Building2 size={12} className="opacity-50" />
                          <span className="truncate">{org.name}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-center text-[10px] text-zinc-500 italic">
                        No active partners found
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="h-4" />
              <div className="px-3 pb-2 pt-1 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Workspace
              </div>
              <button
                onClick={() => { setView('dashboard'); setIsMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentView === 'dashboard' ? 'bg-zinc-800/50 text-white' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/30'
                }`}
              >
                <LayoutDashboard size={18} className="text-zinc-500" />
                <span>Overview</span>
              </button>
              <button
                onClick={() => { setView('organizations'); setIsMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentView === 'organizations' ? 'bg-zinc-800/50 text-white' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/30'
                }`}
              >
                <Building2 size={18} className="text-zinc-500" />
                <span>Organizations</span>
              </button>
              <button
                onClick={() => { setView('invoices'); setIsMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentView === 'invoices' ? 'bg-zinc-800/50 text-white' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/30'
                }`}
              >
                <Receipt size={18} className="text-zinc-500" />
                <span>Invoices</span>
              </button>
            </div>

            <div className="p-2 border-t border-zinc-800 mt-auto">
              <button
                onClick={() => { setView('settings'); setIsMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentView === 'settings' ? 'bg-zinc-800/50 text-white' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/30'
                }`}
              >
                <SettingsIcon size={18} className="text-zinc-500" />
                <span>Settings</span>
              </button>
              <button
                onClick={() => { handleSignOut(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-400 hover:text-red-400 hover:bg-red-900/10 transition-all duration-200 mt-1"
              >
                <LogOut size={18} />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile header bar */}
        <header className="lg:hidden h-12 bg-zinc-925 border-b border-zinc-800 flex items-center px-4 shrink-0">
          <button
            className="text-zinc-400 hover:text-zinc-100 p-1"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <Menu size={20} />
          </button>
          <span className="ml-3 font-semibold text-zinc-100 text-sm">cfleet client</span>
        </header>

        <main className="flex-1 min-w-0 relative bg-white overflow-y-auto">{renderContent()}</main>
      </div>
    </div>
  );
}

/**
 * Main App Component
 *
 * Sets up providers and renders the application.
 * - BrowserRouter for client-side routing
 * - QueryClientProvider for TanStack Query data fetching
 * - AuthProvider for authentication (using customer appType)
 * - ProtectedRoute for route protection
 */
function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider
          apiBaseUrl={API_BASE_URL}
          appType="customer"
          authProvider={AUTH_PROVIDER}
          betterAuthUrl={BETTER_AUTH_URL || undefined}
        >
          <Routes>
            {/* Public auth routes */}
            <Route path="/login" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute loginPath="/login">
                  <CustomerPortal />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
